import React, { useState, useRef } from 'react';
import imageCompression from 'browser-image-compression';
import { supabase } from '../supabaseClient';

const MAX_CHARS = 2000;
const MAX_FILE_MB = 8;
const MAX_PHOTOS = 5;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png'];

export default function FormOverlay({ theme, submitted, confirmMsg, onSubmitSuccess, onSkip }) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const isDark = theme === 'dark';

  const handleFile = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const total = photos.length + files.length;
    if (total > MAX_PHOTOS) {
      setError(`You can upload up to ${MAX_PHOTOS} photos. You already have ${photos.length} selected.`);
      e.target.value = '';
      return;
    }

    for (const file of files) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError('Only JPG and PNG files are accepted.');
        e.target.value = '';
        return;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        setError(`Each photo must be under ${MAX_FILE_MB}MB.`);
        e.target.value = '';
        return;
      }
    }

    setError('');
    try {
      const compressed = await Promise.all(
        files.map(file =>
          imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1200, useWebWorker: true })
            .then(c => ({ file: c, name: file.name }))
        )
      );
      setPhotos(prev => [...prev, ...compressed]);
    } catch {
      setError('Failed to process one or more images. Please try again.');
    }
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() && !message.trim() && photos.length === 0) {
      setError('Please add a name, message, or photo before posting.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const photo_urls = [];

      for (const { file } of photos) {
        const ext = file.type === 'image/png' ? 'png' : 'jpg';
        const uploadName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('photos')
          .upload(uploadName, file, { contentType: file.type });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('photos')
          .getPublicUrl(uploadName);
        photo_urls.push(urlData.publicUrl);
      }

      const { error: insertError } = await supabase
        .from('posts')
        .insert([{
          name: name.trim() || null,
          message: message.trim() || null,
          photo_url: photo_urls[0] ?? null,
          photo_urls: photo_urls.length > 0 ? photo_urls : null,
        }]);

      if (insertError) throw insertError;

      onSubmitSuccess();
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const c = {
    overlay: {
      position: 'fixed',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '1rem',
      background: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
    },
    card: {
      background: isDark ? '#0a0a0a' : '#fff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      borderRadius: '1rem',
      padding: '2rem',
      width: '100%',
      maxWidth: '440px',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
      maxHeight: '90vh',
      overflowY: 'auto',
    },
    heading: {
      fontSize: '1rem',
      fontWeight: 300,
      color: isDark ? '#fff' : '#000',
      lineHeight: 1.7,
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.4rem',
      fontSize: '0.85rem',
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
      letterSpacing: '0.05em',
    },
    input: {
      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
      borderRadius: '0.5rem',
      padding: '0.65rem 0.85rem',
      color: isDark ? '#fff' : '#000',
      fontSize: '1rem',
      outline: 'none',
      width: '100%',
      transition: 'border-color 0.2s',
    },
    textarea: {
      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
      borderRadius: '0.5rem',
      padding: '0.65rem 0.85rem',
      color: isDark ? '#fff' : '#000',
      fontSize: '1rem',
      outline: 'none',
      width: '100%',
      resize: 'none',
      minHeight: '100px',
      transition: 'border-color 0.2s',
    },
    charCount: {
      textAlign: 'right',
      fontSize: '0.78rem',
      color: message.length > MAX_CHARS * 0.9
        ? '#00C4C8'
        : isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
    },
    photoButton: {
      background: 'none',
      border: `1px dashed ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
      borderRadius: '0.5rem',
      padding: '0.65rem 1rem',
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
      fontSize: '0.9rem',
      cursor: 'pointer',
      width: '100%',
      textAlign: 'left',
      transition: 'border-color 0.2s',
    },
    photoList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.4rem',
      marginTop: '0.5rem',
    },
    photoItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '0.85rem',
      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)',
      background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
      borderRadius: '0.4rem',
      padding: '0.4rem 0.7rem',
    },
    removeBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)',
      fontSize: '1rem',
      lineHeight: 1,
      padding: '0 0.2rem',
    },
    submitButton: {
      background: '#00C4C8',
      color: '#000',
      border: 'none',
      borderRadius: '0.5rem',
      padding: '0.8rem',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: loading ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.7 : 1,
      transition: 'opacity 0.2s',
      width: '100%',
    },
    skip: {
      textAlign: 'center',
      fontSize: '0.85rem',
      color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      textDecoration: 'underline',
      textDecorationColor: 'transparent',
      transition: 'color 0.2s',
    },
    error: {
      color: '#ff6b6b',
      fontSize: '0.85rem',
    },
    confirm: {
      fontSize: '1.2rem',
      color: isDark ? '#fff' : '#000',
      textAlign: 'center',
      lineHeight: 1.6,
    },
  };

  if (submitted) {
    return (
      <div style={c.overlay}>
        <div style={c.card}>
          <p style={c.confirm}>{confirmMsg}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={c.overlay}>
      <div style={c.card}>
        <p style={c.heading}>After 15 years in SOMA, we are closing our doors. United Barbell was never just a gym. It was the people who showed up, pushed each other, cheered loudly and built community together. If you were ever part of it, we are so grateful. Share a memory below and let us know you were here 🩵</p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <label style={c.label}>
            NAME <span style={{ fontStyle: 'italic', opacity: 0.7 }}>(optional)</span>
            <input
              style={c.input}
              type="text"
              placeholder="Your name"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={100}
            />
          </label>

          <label style={c.label}>
            MESSAGE <span style={{ fontStyle: 'italic', opacity: 0.7 }}>(optional)</span>
            <textarea
              style={c.textarea}
              placeholder="Leave a message for the community…"
              value={message}
              onChange={e => {
                if (e.target.value.length <= MAX_CHARS) setMessage(e.target.value);
              }}
            />
            <span style={c.charCount}>{message.length} / {MAX_CHARS}</span>
          </label>

          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              style={{ display: 'none' }}
              onChange={handleFile}
            />
            {photos.length < MAX_PHOTOS && (
              <button
                type="button"
                style={c.photoButton}
                onClick={() => fileRef.current.click()}
              >
                + Add photos (optional){photos.length > 0 ? ` — ${MAX_PHOTOS - photos.length} remaining` : ''}
                <span style={{ display: 'block', fontSize: '0.78rem', marginTop: '0.25rem', opacity: 0.6, fontStyle: 'italic' }}>
                  one photo at a time looks best on the wall 🙏
                </span>
              </button>
            )}
            {photos.length > 0 && (
              <div style={c.photoList}>
                {photos.map((p, i) => (
                  <div key={i} style={c.photoItem}>
                    <span>📎 {p.name}</span>
                    <button
                      type="button"
                      style={c.removeBtn}
                      onClick={() => removePhoto(i)}
                      aria-label="Remove photo"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <p style={c.error}>{error}</p>}

          <button type="submit" style={c.submitButton} disabled={loading}>
            {loading ? 'Posting…' : 'Post to the Wall'}
          </button>
        </form>

        <button
          style={c.skip}
          onClick={onSkip}
          onMouseEnter={e => (e.target.style.color = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)')}
          onMouseLeave={e => (e.target.style.color = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)')}
        >
          Skip, just show me the wall
        </button>
      </div>
    </div>
  );
}
